import { Box, Progress, Tag, TagLabel, Text, Tooltip } from "@chakra-ui/react";
import * as CSS from "csstype";
import { Feature, Geometry } from "geojson";

import { useFileUpload } from "../context";
import { SceneFeature, ModelRun } from "../types/scenes";
import { UploadStatus } from "../types/uploads";
import { CollecticonCircleInformation } from "@devseed-ui/collecticons-chakra";

const statusColors = {
  pending: "purple",
  started: "secondary",
  pansharpening_started: "secondary",
  pansharpening_complete: "secondary",
  inferencing_started: "secondary",
  "inferencing failed": "red",
  "pansharpening failed": "red",
  succeeded: "green",
};

const tagStyle = {
  size: "sm",
  borderRadius: "full",
  fontSize: "xs",
  fontFamily: "mono",
  whiteSpace: "pre",
  letterSpacing: "0.25px",
  textTransform: "uppercase" as CSS.Property.TextTransform,
};

interface IModelStatusTag {
  run: ModelRun;
}

export function ModelStatusTag({ run }: IModelStatusTag) {
  if(!run.status.length) return null;

  return (
    <Tag
      colorScheme={statusColors[run.status]}
      display="inline-flex"
      alignItems="center"
      {...tagStyle}
    >
      Model {run.status}
      {Number(run.status_reason?.length) > 0 && (
        <Tooltip label={run.status_reason}>
          <CollecticonCircleInformation size="3" ml="2" />
        </Tooltip>
      )}
    </Tag>
  );
}

type Props = {
  scene: Feature<Geometry, SceneFeature>;
};

function SceneStatusTag({ scene }: Props) {
  const { is_valid, validation_error, model_runs } = scene.properties;
  const { status: uploadStatus = {} } = useFileUpload();
  const sceneUploadStatus = uploadStatus[scene?.properties.s3_prefix];

  // File upload in progress
  const hasUploadsInProgress = sceneUploadStatus?.multiSpectral === UploadStatus.Uploading || sceneUploadStatus?.panchromatic === UploadStatus.Uploading;
  if (hasUploadsInProgress) {
    const totalUploaded = Object.values(sceneUploadStatus.progress).reduce((acc, curr) => acc + curr, 0);
    const progressPercent = Math.round(totalUploaded / sceneUploadStatus.totalSize * 100);
    return (
      <Box textAlign="center" width="100%">
        <Text fontSize="xs" color="base.200">
          {`Uploads pending (${progressPercent}%)`}
        </Text>
        <Progress
          value={totalUploaded / sceneUploadStatus.totalSize * 100}
          width="100%"
          colorScheme="primary"
          size="sm"
          borderRadius="4"
        />
      </Box>
    );
  }

  // Invalid uploads
  if (!is_valid && validation_error) {
    return (
      <Tag {...tagStyle} colorScheme="red">
        <TagLabel>Uploads invalid</TagLabel>
        <Tooltip label={validation_error}>
          <CollecticonCircleInformation size="3" ml="2" />
        </Tooltip>
      </Tag>
    );
  }

  // Find the first pending model run
  const pendingModelRun = model_runs.find((run) => {
    return run.status !== "succeeded" && run.status.indexOf("failed") === -1;
  });

  if (!pendingModelRun) return null;

  const { status } = pendingModelRun;

  if (status === "pending") {
    return (
      <Tag {...tagStyle} colorScheme={statusColors.pending}>
        <TagLabel>Awaiting model run</TagLabel>
      </Tag>
    );
  }

  if (status === "started") {
    return (
      <Tag {...tagStyle} colorScheme={statusColors.pending}>
        <TagLabel>Model run started</TagLabel>
      </Tag>
    );
  }

  if (status === "pansharpening_started") {
    if (pendingModelRun.details?.pansharpening) {
      const step = pendingModelRun.details.pansharpening[pendingModelRun.details.pansharpening.length - 1];
      const {step_name, step_number, total_steps} = step;
      return (
        <Box textAlign="center" width="100%">
          <Text fontSize="xs" lineHeight="1" color="base.200" mb={1}>{step_name} <br /> Step {step_number} of {total_steps}</Text>
          <Progress
            value={(step_number - 1) / total_steps * 100}
            width="100%"
            colorScheme="primary"
            size="sm"
            borderRadius="4"
          />
        </Box>
      );
    } else {
      return (
        <Box textAlign="center" width="100%">
          <Text fontSize="xs" color="base.200">Initializing pansharpening</Text>
          <Progress
            value={0}
            width="100%"
            colorScheme="primary"
            size="sm"
            borderRadius="4"
          />
        </Box>
      );
    }
  }

  if (status === "inferencing_started") {
    if (pendingModelRun.details?.inferencing) {
      const step = pendingModelRun.details.inferencing[pendingModelRun.details.inferencing.length - 1];
      const {step_name, step_number, total_steps} = step;
      return (
        <Box textAlign="center" width="100%">
          <Text fontSize="xs" lineHeight="1" color="base.200" mb={1}>{step_name} <br /> Step {step_number} of {total_steps}</Text>
          <Progress
            value={(step_number - 1) / total_steps * 100}
            width="100%"
            colorScheme="primary"
            size="sm"
            borderRadius="4"
          />
        </Box>
      );
    } else {
      return (
        <Box textAlign="center" width="100%">
          <Text fontSize="xs" color="base.200">Initializing inferencing</Text>
          <Progress
            value={0}
            width="100%"
            colorScheme="primary"
            size="sm"
            borderRadius="4"
          />
        </Box>
      );
    }
  }

  const orderedModelRuns = model_runs.sort(
    (a, b) => new Date(b.date).valueOf() - new Date(a.date).valueOf()
  );

  const latestModelRun = orderedModelRuns[0];
  if (!model_runs.length) return null;
  const hasSuccessModelRuns = model_runs.some(({ status }) => status === "succeeded");
  if (hasSuccessModelRuns) return null;
  if (latestModelRun.status !== "succeeded") {
    return (
      <ModelStatusTag run={latestModelRun} />
    );
  }

  return null;
}

export default SceneStatusTag;
